import React, { useEffect, useRef, useState } from 'react';
import {
  withStyles,
  FormControl,
  FormLabel,
  FormGroup,
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PF2MButton from '~/components/PF2MButton';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import { addContentVariable } from '~/store/quality/actions';
import PF2MDatePickerOutlined from '~/components/PF2MDatePickerOutlined';
import { newMomentDate, formatDateToServerFormat, formatTime } from '~/utils/moment';
import styles from '~/pages/Quality/styles';
import PF2MOutlinedInput from '~/components/PF2MOutlinedInput';
import NumberFormat from 'react-number-format';
import { Field, Form, Formik } from 'formik';
import PF2MDatepickerNew from '~/components/PF2MDatepickerNew';
import usePlatformConfiguration from '~/services/platformConfiguration';

export const analysisTypes = translate => [
  { id: 1, name: translate('common:Global') },
  { id: 2, name: translate('common:Local') },
];

const Header = ({ classes }) => {
  const showAnalysisType = usePlatformConfiguration('grade_control_show_analysis_type');
  const formikRef = useRef();
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const materials = useSelector(state => state.manager.materials);
  const elements = useSelector(state => state.manager.elements.filter(r => r.is_origin));
  const subElements = useSelector(state => state.manager.subElements);
  const contents = useSelector(state => state.quality.contents);
  const contentVariablesFlat = useSelector(state => state.quality.contentVariablesFlat);

  const [analysisType, setAnalysisType] = useState(0);
  const [startDate, setStartDate] = useState(newMomentDate());
  const [endDate, setEndDate] = useState(newMomentDate());
  const [element, setElement] = useState(0);
  const [subElement, setSubElement] = useState(0);
  const [material, setMaterial] = useState(0);
  const [fieldContents, setFieldContents] = useState({});

  const startOfDay = m => m.startOf('day');
  const endOfDay = m => m.endOf('day');

  const resetForm = () => {
    setStartDate(newMomentDate());
    setEndDate(newMomentDate());
    setElement(0);
    setSubElement(0);
    setMaterial(0);
    setAnalysisType(0);
    setFieldContents({});
  };

  useEffect(() => {
    if (contents.length > 0 && Object.keys(fieldContents).length === 0) {
      const newValues = {};
      contents.forEach((c) => {
        newValues[`content_${c.id}_${Number(!!c.value_type)}`] = '';
      });
      setFieldContents(newValues);
    }
  }, [contents]);

  const renderContents = () => (
    contents.map((c, i) => (
      <FormControl
        key={c.id}
        style={{ gridColumnStart: i === 0 ? 1 : '' }}
      >
        <FormLabel className={classes.formLabel}>
          {`${c.name}`}
        </FormLabel>
        <FormGroup>
          {c.value_type !== 1 && (
            <NumberFormat
              value={fieldContents[`content_${c.id}_${Number(!!c.value_type)}`]}
              customInput={PF2MOutlinedInput}
              min={0}
              decimalSeparator={','}
              decimalScale={5}
              className={classes.textField}
              style={{ minWidth: 150, height: 40 }}
              onValueChange={(val) => {
                const newValues = {
                  [`content_${c.id}_${Number(!!c.value_type)}`]: val.formattedValue,
                };
                setFieldContents(fc => ({ ...fc, ...newValues }));
              }}
            />
          )}
          {c.value_type === 1 && (
            <PF2MOutlinedInput
              value={fieldContents[`content_${c.id}_${Number(!!c.value_type)}`] || ''}
              className={classes.textField}
              style={{ minWidth: 150, height: 40 }}
              onBlur={(e) => {
                const newValues = {
                  ...fieldContents,
                  [`content_${c.id}_${Number(!!c.value_type)}`]: e.target.value,
                };
                setFieldContents(newValues);
              }}
            />
          )}
        </FormGroup>
      </FormControl>
    ))
  );

  const renderItems = (items = []) => [
    { value: 0, label: translate('common:Select') },
    ...items.map(e => ({
      value: e.id,
      label: e.name,
    })),
  ];

  const formSubmit = (e) => {
    const data = {
      analysis_type_id: e.analysis_type,
      start_date: formatDateToServerFormat(
        e.analysis_type === 2 ? startDate : startOfDay(startDate),
      ),
      start_timestamp: e.analysis_type === 2 ? startDate.unix() : startOfDay(startDate).unix(),
      start_time: formatTime(e.analysis_type === 2 ? startDate : startOfDay(startDate)),
      end_date: formatDateToServerFormat(
        e.analysis_type === 2 ? endDate : endOfDay(endDate),
      ),
      end_time: formatTime(e.analysis_type === 2 ? endDate : endOfDay(endDate)),
      end_timestamp: e.analysis_type === 2 ? endDate.unix() : endOfDay(endDate).unix(),
      element_id: e.element,
      element_point_id: e.sub_element,
      material_id: e.material,
      ...fieldContents,
    };

    dispatch(addContentVariable(data));
    resetForm();
  };

  const renderFormGroupDateField = ({
    label,
    value,
    onChange,
    fieldName,
    isDateTime,
    ...datePickerProps
  }) => (
    <FormControl ref={formikRef.current}>
      <FormLabel className={classes.formLabel}>{label}</FormLabel>
      <FormGroup>
        <Field
          name={fieldName}
          render={({ field, form }) => {
            const style = { height: 40, minWidth: 150 };

            let hasError = false;
            const error = form.errors[fieldName];

            if ('content' in form.errors || error) {
              hasError = true;
            }

            return (
              <PF2MDatepickerNew
                {...field}
                field={fieldName}
                isFormControlled={false}
                form={form}
                views={['year', 'month', 'date', ...(isDateTime ? ['hours', 'minutes'] : [])]}
                isDateTime={isDateTime}
                format={isDateTime ? 'DD/MM/yyyy HH:mm' : 'DD/MM/yyyy'}
                value={value}
                disableFuture={false}
                helperText={error || ''}
                error={hasError}
                InputProps={{ style }}
                onChange={(e) => {
                  onChange(e);
                  form.setFieldValue(fieldName, e, true);
                }}
                style={{
                  width: '100%',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  height: 38,
                  paddingLeft: 4,
                }}
                {...datePickerProps}
              />
            );
          }}
        />
      </FormGroup>
    </FormControl>
  );

  const renderFormGroupSelectField = ({
    label,
    value,
    onChange,
    fieldName,
    items,
    ...searchSelectProps
  }) => (
    <FormControl>
      <FormLabel className={classes.formLabel}>
        {label}
      </FormLabel>
      <FormGroup>
        <Field
          name={fieldName}
          render={({ field, form }) => {
            let className = classes.fieldSelect;
            const error = form.errors[fieldName];

            if (form.errors.content || error) {
              className += ` ${classes.fieldError}`;
            }

            return (
              <div>
                <PF2MSearchSelectOutlined
                  {...field}
                  value={value}
                  onChange={({ target: { value: targetValue } }) => {
                    onChange(targetValue);
                    form.setFieldValue(fieldName, targetValue);
                  }}
                  className={className}
                  {...searchSelectProps}
                >
                  {renderItems(items)}
                </PF2MSearchSelectOutlined>
                {error && <p className={classes.errorMessage}>{error}</p>}
              </div>
            );
          }}
        />
      </FormGroup>
    </FormControl>
  );

  // eslint-disable-next-line max-len
  const checkDuplicateRegistration = (newItem, ignoreId = null) => contentVariablesFlat.some((item) => {
    if (ignoreId !== null && item.id === ignoreId) return false;

    const isSameElementAndMaterial = (
      item.element_point_id === newItem.element_point_id
      && item.material_id === newItem.material_id
    );

    const isSameAnalysisType = item.analysis_type_id === newItem.analysis_type_id;

    const isOverlappingPeriod = newItem.analysis_type_id !== 2 ? (
      (newItem.start_timestamp >= item.start_timestamp
        && newItem.start_timestamp <= item.end_timestamp)
      || (newItem.end_timestamp >= item.start_timestamp
        && newItem.end_timestamp <= item.end_timestamp)
      || (newItem.start_timestamp <= item.start_timestamp
        && newItem.end_timestamp >= item.end_timestamp)
    ) : (newItem.start_timestamp === item.start_timestamp
      && newItem.end_timestamp === item.end_timestamp);

    return isSameElementAndMaterial && isSameAnalysisType && isOverlappingPeriod;
  });

  const validateContentAlreadyRegistered = ({
    formStartDate,
    formEndDate,
    formElement,
    formSubElement,
    formMaterial,
    formAnalysisType,
  }) => {
    const isDateTime = formAnalysisType === 2;
    const existingValidation = contentVariablesFlat.find(
      (c) => {
        const contentStartTimestamp = isDateTime
          ? moment.unix(c.start_timestamp)
          : moment.unix(c.start_timestamp).startOf('day');
        const contentEndTimestamp = isDateTime
          ? moment.unix(c.end_timestamp)
          : moment.unix(c.end_timestamp).endOf('day');

        const checkStartTimestamp = !isDateTime ? (
          contentStartTimestamp.isBetween(formStartDate, formEndDate)
          || formStartDate.isBetween(contentStartTimestamp, contentEndTimestamp)
        ) : contentStartTimestamp.isSame(formStartDate);
        const checkEndTimestamp = !isDateTime ? (
          contentEndTimestamp.isBetween(formStartDate, formEndDate)
          || formEndDate.isBetween(contentStartTimestamp, contentEndTimestamp)
        ) : contentEndTimestamp.isSame(formEndDate);

        console.log(isDateTime, contentStartTimestamp.format(), formStartDate.format(),
          checkStartTimestamp, checkEndTimestamp);

        return c.element_id === formElement
          && c.element_point_id === formSubElement
          && c.material_id === formMaterial
          && c.analysis_type_id === formAnalysisType
          && (checkStartTimestamp || checkEndTimestamp);
      },
    );

    console.log('existingValidation', existingValidation);

    if (existingValidation) {
      return existingValidation;
    }

    const newItem = {
      analysis_type_id: formAnalysisType,
      element_point_id: formSubElement,
      material_id: formMaterial,
      start_timestamp: formStartDate.unix(),
      end_timestamp: formEndDate.unix(),
    };

    const duplicate = checkDuplicateRegistration(newItem);

    return duplicate ? {} : null;
  };

  return (
    <>
      <div style={{
        border: '1px solid #CDCDCD',
        borderRadius: 5,
        padding: 10,
      }}
      >
        <Formik
          ref={formikRef}
          validateOnChange
          validate={({
            analysis_type: formAnalysisType,
            start_date: formStartDate,
            end_date: formEndDate,
            element: formElement,
            sub_element: formSubElement,
            material: formMaterial,
          }) => {
            const errors = {};

            const defaultStartTimestamp = startOfDay(newMomentDate());
            const defaultEndTimestamp = endOfDay(newMomentDate());

            const startTimestamp = formStartDate && formAnalysisType === 2
              ? formStartDate : startOfDay(formStartDate);
            const endTimestamp = formEndDate && formAnalysisType === 2
              ? formEndDate : endOfDay(formEndDate);

            const formData = {
              formStartDate: startTimestamp || defaultStartTimestamp,
              formEndDate: endTimestamp || defaultEndTimestamp,
              formElement,
              formSubElement,
              formMaterial,
              formAnalysisType,
            };

            const contentAlreadyRegistered = validateContentAlreadyRegistered(formData);

            if (contentAlreadyRegistered !== null) {
              errors.content = translate('validation:ContentAlreadyRegistered');
            }

            if (startTimestamp > endTimestamp) {
              errors.start_date = translate('validation:InitialDateGreaterThanStartDate');
              errors.end_date = translate('validation:FinalDateSmallerThanInitialDate');
            }

            if (['undefined', '0'].includes(String(formElement))) {
              errors.element = translate('validation:RequiredField');
            }

            if (['undefined', '0'].includes(String(formSubElement))) {
              errors.sub_element = translate('validation:RequiredField');
            }

            if (['undefined', '0'].includes(String(formMaterial))) {
              errors.material = translate('validation:RequiredField');
            }

            return errors;
          }}
          onSubmit={(values, action) => {
            formSubmit(values);
            action.resetForm({
              values: {
                start_date: newMomentDate(),
                end_date: newMomentDate(),
                element: 0,
                sub_element: 0,
                material: 0,
              },
            });
          }}
        >
          {({
            errors,
            isValid,
            isSubmitting,
            submitForm,
          }) => (
            <Form style={{
              display: 'flex',
              alignItems: 'left',
              flexDirection: 'column',
            }}
            >
              <span className={classes.labelTypeSelector}>
                {translate('common:Associations')}
              </span>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
              }}
              >
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 200px)',
                  gridColumnGap: 10,
                  gridRowGap: 10,
                  margin: '10px 0',
                }}
                >
                  {showAnalysisType && renderFormGroupSelectField({
                    label: translate('common:AnalysisType'),
                    fieldName: 'analysis_type',
                    value: analysisType,
                    items: analysisTypes(translate),
                    onChange: setAnalysisType,
                  })}
                  {renderFormGroupDateField({
                    label: translate('common:StartDate'),
                    value: startDate,
                    onChange: setStartDate,
                    fieldName: 'start_date',
                    isDateTime: analysisType === 2,
                  })}
                  {renderFormGroupDateField({
                    label: translate('common:EndDate'),
                    value: endDate,
                    onChange: setEndDate,
                    fieldName: 'end_date',
                    isDateTime: analysisType === 2,
                  })}
                  {renderFormGroupSelectField({
                    fieldName: 'element',
                    label: translate('common:Element'),
                    value: element,
                    items: elements,
                    onChange: setElement,
                  })}
                  {renderFormGroupSelectField({
                    fieldName: 'sub_element',
                    label: translate('common:SubElement'),
                    value: subElement,
                    items: subElements.filter(s => s.id_element === element),
                    onChange: setSubElement,
                  })}
                  {renderFormGroupSelectField({
                    fieldName: 'material',
                    label: translate('common:Material'),
                    value: material,
                    items: materials,
                    onChange: setMaterial,
                  })}
                  {
                    errors && errors.content && (
                      <div style={{ gridColumn: '1 / span 5' }}>
                        <p className={classes.errorMessage}>
                          {errors.content}
                        </p>
                      </div>
                    )
                  }
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: 10,
                  margin: '10px 0',
                }}
                >
                  {renderContents()}
                </div>
              </div>
              <div style={{
                alignItems: 'center',
                placeSelf: 'self-end',
                marginLeft: 'auto',
              }}
              >
                <PF2MButton
                  style={{ marginTop: 15 }}
                  onClick={submitForm}
                  disabled={isSubmitting || !isValid}
                >
                  <Add className={classes.icon} />
                  {translate('common:Register')}
                </PF2MButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

Header.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Header);
